'use client';

import { goMainOneTimeAuth } from '@/utils/functions';
import {
  type CoinSection,
  CommonMenu as CommonMenuComponent,
  type CommonMenuProps,
  type MenuItem,
  type ProfileSection,
  formatCurrency,
  iconArrowRotate,
  iconCard,
  iconCoin,
  iconCoinBlanc,
  iconCoinSmall,
  iconEdit,
  iconHistory,
  iconLogin,
  iconLogout,
  iconMailBox,
  iconPresent,
  iconQuestion,
  iconSetting,
  iconShipping,
  iconWChance,
} from '@monorepo/common';
import type { Route } from 'next';
import { useRouter } from 'next/navigation';

export { useIsMenuOpenedAtom } from '@monorepo/common';

function CommonMenu({
  isOptionNotEntered = false,
  hasPendingEntry = false,
  onLogout,
  userUnreadMessageBoxCount = 0,
  userCoin,
  userName,
  formatCurrency: formatCurrencyProp,
  serviceId,
  locationHref,
}: {
  isOptionNotEntered?: boolean;
  hasPendingEntry?: boolean;
  onLogout: () => void;
  userUnreadMessageBoxCount?: number;
  userCoin?: number;
  userName?: string;
  formatCurrency?: CommonMenuProps['formatCurrency'];
  serviceId?: string;
  locationHref?: string;
}) {
  const router = useRouter();
  const finalFormatCurrency = formatCurrencyProp || formatCurrency;
  const finalServiceId = serviceId || process.env.NEXT_PUBLIC_SERVICE_ID || 'fanychance';
  const finalLocationHref = locationHref || (typeof window !== 'undefined' ? window.location.href : '');

  // プロフィールセクションの生成
  const profileSection: ProfileSection = {
    userName: userName || '',
    editLink: '/account/edit',
    editIconSrc: iconEdit.src,
  };

  // コインセクションの生成
  const coinSection: CoinSection = {
    label: '保有しているコイン',
    coinAmount: userCoin ?? 0,
    coinIconSrc: iconCoinSmall.src,
    formatCurrency: finalFormatCurrency,
  };

  // メニューリストの生成
  const menuItems: MenuItem[] = [
    {
      id: 'messages',
      iconSrc: iconMailBox.src,
      text: 'メッセージボックス',
      onClick: () => router.push('/account/messages'),
      showBadge: userUnreadMessageBoxCount > 0,
    },
    {
      id: 'collection',
      iconSrc: iconPresent.src,
      text: '獲得した景品一覧',
      onClick: () => router.push('/account/collection'),
      showBadge: isOptionNotEntered,
    },
    {
      id: 'wchance',
      iconSrc: iconWChance.src,
      text: 'Wチャンス賞チャレンジ履歴',
      onClick: () => router.push('/account/wchance-collection'),
      showBadge: hasPendingEntry,
    },
    {
      id: 'purchase',
      iconSrc: iconCoin.src,
      text: 'コインを購入する',
      onClick: () => router.push('/account/purchase'),
    },
    {
      id: 'coin-history',
      iconSrc: iconCoinBlanc.src,
      text: 'コイン消費履歴',
      onClick: () => router.push('/account/coin-history'),
    },
    {
      id: 'payment-cards',
      iconSrc: iconCard.src,
      text: 'お支払い方法',
      onClick: () => goMainOneTimeAuth({ redirectPath: `/${finalServiceId}/payment/cards` }),
    },
    {
      id: 'payment-history',
      iconSrc: iconHistory.src,
      text: '決済履歴',
      onClick: () => goMainOneTimeAuth({ redirectPath: '/account/payment/history' }),
    },
    {
      id: 'address',
      iconSrc: iconShipping.src,
      text: '配送先の確認と変更',
      onClick: () => goMainOneTimeAuth({ redirectPath: `/account/address?callback=${finalLocationHref}` }),
    },
    {
      id: 'login-settings',
      iconSrc: iconLogin.src,
      text: 'ログイン情報設定',
      onClick: () => goMainOneTimeAuth({ redirectPath: '/account' }),
    },
    {
      id: 'notification',
      iconSrc: iconSetting.src,
      text: '通知設定',
      onClick: () => router.push('/account/notification'),
    },
    {
      id: 'faq',
      iconSrc: iconQuestion.src,
      text: 'よくある質問',
      onClick: () => router.push('/faq'),
    },
    {
      id: 'logout',
      iconSrc: iconLogout.src,
      text: 'ログアウト',
      onClick: onLogout,
    },
  ];

  return (
    <CommonMenuComponent
      isOptionNotEntered={isOptionNotEntered}
      hasPendingEntry={hasPendingEntry}
      onLogout={onLogout}
      userUnreadMessageBoxCount={userUnreadMessageBoxCount}
      userCoin={userCoin}
      userName={userName}
      formatCurrency={finalFormatCurrency}
      menuItems={menuItems}
      profileSection={profileSection}
      coinSection={coinSection}
    />
  );
}

export default CommonMenu;
